import React, { useState } from 'react';
import { AlertTriangle, X, Users, ShieldAlert, UserCheck, Search, Loader2 } from 'lucide-react';
import { useEmergency } from '../../context/EmergencyContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import type { EmergencyTargetScope } from '@sahaj/types';

export const SendEmergencyModal: React.FC = () => {
  const { isSendModalOpen, closeSendModal } = useEmergency();
  const { currentWorkspace, members } = useWorkspace();
  const { user } = useAuth();

  const [message, setMessage] = useState('');
  const [targetScope, setTargetScope] = useState<EmergencyTargetScope>('ADMINS');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isSendModalOpen || !currentWorkspace || !user) return null;

  // Determine current user workspace role level
  const currentMember = members.find((m) => m.user?.id?.toLowerCase() === user.id.toLowerCase());
  const isWorkspaceAdmin = currentMember?.role === 'ADMIN' || currentMember?.role === 'OWNER';

  const otherMembers = members.filter((m) => m.user?.id && m.user.id.toLowerCase() !== user.id.toLowerCase());
  const filteredMembers = otherMembers.filter((m) => {
    const name = m.user?.displayName || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      setErrorMsg('Please enter an emergency message.');
      return;
    }
    if (targetScope === 'SPECIFIC_USERS' && selectedUserIds.length === 0) {
      setErrorMsg('Please select at least one recipient.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      const payload: any = {
        message: message.trim(),
        targetScope,
      };
      if (targetScope === 'SPECIFIC_USERS') {
        payload.recipientUserIds = selectedUserIds;
      }

      await api.emergencyAlerts.create(currentWorkspace.id, payload);

      setMessage('');
      setSelectedUserIds([]);
      closeSendModal();
    } catch (err: any) {
      console.error('[SendEmergencyModal] Failed to send emergency alert', err);
      setErrorMsg(err.message || 'Failed to send emergency alert.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-lg bg-card border-2 border-destructive/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-destructive/10 border-b border-destructive/30 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/20 text-destructive rounded-xl animate-pulse">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Trigger 🚨 Emergency Alert</h2>
              <p className="text-xs text-muted-foreground">Broadcast an urgent notification to workspace recipients</p>
            </div>
          </div>
          <button
            onClick={closeSendModal}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
          {errorMsg && (
            <div className="p-3 bg-destructive/15 border border-destructive/40 text-destructive rounded-xl text-xs font-medium">
              {errorMsg}
            </div>
          )}

          {/* Message textarea */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Emergency Message <span className="text-destructive">*</span>
            </label>
            <textarea
              rows={3}
              maxLength={1000}
              placeholder="e.g. Please come forward immediately. Emergency situation in Room 204."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full p-3 bg-muted/50 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-destructive resize-none"
            />
            <div className="text-[10px] text-muted-foreground text-right mt-1">
              {message.length}/1000 chars
            </div>
          </div>

          {/* Scope Selector */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-2">
              Select Recipients Target Scope <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={() => setTargetScope('ADMINS')}
                className={`p-3 rounded-xl border text-left flex flex-col justify-between transition ${
                  targetScope === 'ADMINS'
                    ? 'border-destructive bg-destructive/10 text-foreground font-semibold'
                    : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'
                }`}
              >
                <ShieldAlert className="h-4 w-4 text-destructive mb-2" />
                <span className="text-xs">Administrators</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">Admins & Owner only</span>
              </button>

              <button
                type="button"
                disabled={!isWorkspaceAdmin}
                onClick={() => isWorkspaceAdmin && setTargetScope('EVERYONE')}
                className={`p-3 rounded-xl border text-left flex flex-col justify-between transition ${
                  !isWorkspaceAdmin
                    ? 'opacity-40 cursor-not-allowed border-border bg-muted/10'
                    : targetScope === 'EVERYONE'
                    ? 'border-destructive bg-destructive/10 text-foreground font-semibold'
                    : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'
                }`}
              >
                <Users className="h-4 w-4 text-destructive mb-2" />
                <span className="text-xs">Everyone</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">Entire Workspace</span>
              </button>

              <button
                type="button"
                onClick={() => setTargetScope('SPECIFIC_USERS')}
                className={`p-3 rounded-xl border text-left flex flex-col justify-between transition ${
                  targetScope === 'SPECIFIC_USERS'
                    ? 'border-destructive bg-destructive/10 text-foreground font-semibold'
                    : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted'
                }`}
              >
                <UserCheck className="h-4 w-4 text-destructive mb-2" />
                <span className="text-xs">Specific People</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">Select Members</span>
              </button>
            </div>
          </div>

          {/* Member Picker for SPECIFIC_USERS */}
          {targetScope === 'SPECIFIC_USERS' && (
            <div className="space-y-2 pt-1 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground">Select Recipient Members</span>
                <span className="text-xs text-muted-foreground">{selectedUserIds.length} selected</span>
              </div>

              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-8 pl-8 pr-3 bg-muted/50 border border-border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-destructive"
                />
              </div>

              <div className="max-h-40 overflow-y-auto divide-y divide-border border border-border rounded-xl">
                {filteredMembers.map((member) => {
                  const u = member.user;
                  if (!u) return null;
                  const isSelected = selectedUserIds.includes(u.id);
                  return (
                    <div
                      key={u.id}
                      onClick={() => toggleUserSelection(u.id)}
                      className={`p-2.5 flex items-center justify-between cursor-pointer transition text-xs ${
                        isSelected ? 'bg-destructive/10' : 'hover:bg-muted/40'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        {u.avatarUrl ? (
                          <img src={u.avatarUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-[10px]">
                            {u.displayName.charAt(0)}
                          </div>
                        )}
                        <div>
                          <div className="font-semibold text-foreground">{u.displayName}</div>
                          <div className="text-[10px] text-muted-foreground capitalize">{member.role.toLowerCase()}</div>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="h-4 w-4 rounded accent-destructive"
                      />
                    </div>
                  );
                })}

                {filteredMembers.length === 0 && (
                  <div className="p-4 text-center text-xs text-muted-foreground">No members found</div>
                )}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="pt-3 border-t border-border flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={closeSendModal}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs font-bold rounded-xl shadow-lg shadow-destructive/20 flex items-center gap-2 transition disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Sending Alert...</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>Send Emergency Alert</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
